/**
 * OTP — Produtos em vídeo.
 *
 * Scoped to <otp-video-products>. Touches nothing else on the page.
 *
 * Loading strategy, in three stages, so a homepage with 20 cards never pulls
 * 20 videos at once:
 *   1. far away      -> no src at all, only the poster is painted
 *   2. within 400px  -> src attached, preload="metadata"
 *   3. >=35% visible -> play; once it leaves again, pause
 */

class OtpVideoProducts extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';

    this.track = this.querySelector('[data-track]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.videos = Array.from(this.querySelectorAll('video[data-src]'));
    this.autoplay = this.dataset.autoplay !== 'false';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.applyLabels();
    this.bindArrows();
    this.bindSound();
    if (this.autoplay && !this.reduced) this.observeVideos();
  }

  /* ---------- stage 1 + 2: attach sources near the viewport ---------- */
  observeVideos() {
    const attach = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.attachSource(entry.target);
          attach.unobserve(entry.target);
        });
      },
      { rootMargin: '400px 0px' }
    );

    /* ---------- stage 3: play only what is actually on screen ---------- */
    const playback = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            this.attachSource(video);
            const attempt = video.play();
            if (attempt && attempt.catch) attempt.catch(() => {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.35 }
    );

    this.videos.forEach((video) => {
      attach.observe(video);
      playback.observe(video);
    });

    this.observers = [attach, playback];
  }

  attachSource(video) {
    if (video.dataset.attached) return;
    video.dataset.attached = 'true';
    /* phones get the light rendition, wide screens the sharper one */
    const wide = window.matchMedia('(min-width: 750px)').matches;
    const source = document.createElement('source');
    source.src = (wide && video.dataset.srcLarge) || video.dataset.src;
    if (video.dataset.type) source.type = video.dataset.type;
    video.appendChild(source);
    video.preload = 'metadata';
    video.load();
    video.addEventListener('loadeddata', () => video.classList.add('is-loaded'), { once: true });
  }

  /* The native quick-add carries the theme's own wording ("Adicionar" /
     "Escolher"). The block's label replaces it without touching the button's
     behaviour, so the variant handling stays exactly as the theme intends. */
  applyLabels() {
    this.cards.forEach((card) => {
      /* the theme hides the wording on phones because its own button is a small
         round overlay; here the button is full width, so the label stays */
      card.querySelectorAll('.add-to-cart-text__content').forEach((slot) => {
        slot.classList.remove('is-visually-hidden-mobile');
      });

      const label = card.dataset.label;
      if (!label) return;
      card.querySelectorAll('.add-to-cart-text__content > span > span').forEach((slot) => {
        slot.textContent = label;
      });
    });
  }

  /* ---------- arrows ---------- */
  bindArrows() {
    const prev = this.querySelector('[data-prev]');
    const next = this.querySelector('[data-next]');
    if (!prev || !next || !this.track) return;

    const step = () => {
      const card = this.cards[0];
      if (!card) return this.track.clientWidth;
      const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
      const per = card.getBoundingClientRect().width + gap;
      /* move a whole screenful, minus one card so context is kept */
      const visible = Math.max(1, Math.floor(this.track.clientWidth / per));
      return per * Math.max(1, visible - 1);
    };

    prev.addEventListener('click', () => this.track.scrollBy({ left: -step(), behavior: 'smooth' }));
    next.addEventListener('click', () => this.track.scrollBy({ left: step(), behavior: 'smooth' }));

    const sync = () => {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      prev.disabled = this.track.scrollLeft <= 2;
      next.disabled = this.track.scrollLeft >= max;
    };
    this.track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  /* ---------- sound: opt-in, and only one card at a time ---------- */
  bindSound() {
    this.querySelectorAll('[data-sound]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const card = button.closest('[data-card]');
        const video = card && card.querySelector('video');
        if (!video) return;

        const turningOn = video.muted;
        if (turningOn) {
          this.videos.forEach((other) => {
            if (other === video) return;
            other.muted = true;
            const otherCard = other.closest('[data-card]');
            const otherButton = otherCard && otherCard.querySelector('[data-sound]');
            if (otherButton) otherButton.setAttribute('aria-pressed', 'false');
          });
        }
        video.muted = !turningOn;
        button.setAttribute('aria-pressed', turningOn ? 'true' : 'false');
        if (turningOn) {
          const attempt = video.play();
          if (attempt && attempt.catch) attempt.catch(() => {});
        }
      });
    });
  }

  disconnectedCallback() {
    (this.observers || []).forEach((observer) => observer.disconnect());
  }
}

if (!customElements.get('otp-video-products')) {
  customElements.define('otp-video-products', OtpVideoProducts);
}
